<template>
  <div v-if="isInternalUser" class="internal-user-info-bar">
    <p v-for="info in userInfoDisplay" :key="info.key" class="internal-user-info-bar__text">
      {{ info.key }}: <span class="internal-user-info-bar__value">{{ info.value }}</span>
    </p>
    <SwitchPlatformMenu />
  </div>
</template>

<script>
/**
 * Internal-user context bar, mirroring va-portal's InternalLoginUserDisplay.vue — shows
 * which provider/practice the internal user is currently acting as, plus the platform
 * switcher (per the Figma prototype, which places the switcher inline at the end of this
 * bar on both Provider Portal and VI).
 *
 * Internal users only; external providers never see it.
 */
import SwitchPlatformMenu from './SwitchPlatformMenu.vue';
import { isInternalUser } from '@/utils/internalUser';

export default {
  name: 'InternalUserInfoBar',
  components: {
    SwitchPlatformMenu,
  },
  computed: {
    userInfo() {
      return this.$store.state.login.userInfo;
    },
    isInternalUser() {
      return isInternalUser(this.userInfo);
    },
    // Same four fields, in the same order, as va-portal's bar. Practice admins show the
    // practice name in place of a person's name (matching va-portal), and clinic_id comes
    // straight off userInfo here rather than from va-portal's separate loginClinic object.
    userInfoDisplay() {
      const info = this.userInfo;
      if (!info) {
        return [];
      }
      const isPracticeAdmin = !!info.user_roles?.includes('clinic');
      const roleDisplay = isPracticeAdmin ? 'Practice Admin' : 'Provider';
      const personName = [info.customer_first_name, info.customer_last_name]
        .filter(Boolean)
        .join(' ') || info.customer_name || '';
      return [
        { key: 'Name', value: isPracticeAdmin ? (info.clinic_name || personName) : personName },
        { key: 'Provider ID', value: info.customer_id || '' },
        { key: 'Practice ID', value: info.clinic_id || '' },
        { key: 'Role', value: roleDisplay },
      ];
    },
  },
};
</script>

<style scoped>
.internal-user-info-bar {
  display: flex;
  align-items: center;
  height: 28px;
  padding-left: 48px;
  background: #f4f4f4;
  /* Own stacking context above page content so the switcher dropdown, which opens
     downward out of this 28px-tall bar, isn't painted behind the router-view below. */
  position: relative;
  z-index: 999;
}

.internal-user-info-bar__text {
  margin: 0 28px 0 0;
  color: #a8a8a8;
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  letter-spacing: 0.1px;
  white-space: nowrap;
}

.internal-user-info-bar__value {
  font-weight: 400;
}
</style>
